import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/button";
import { fetchOrgs, fetchSigners } from "@/features/text-sign/api";
import { filterInputClass } from "@/components/ui/SearchPanel";
import { cn } from "@/lib/utils";

export type PickedEntity = { id: number; name: string };

/**
 * Ô chọn đơn vị / cá nhân giống hệ cũ: một ô chỉ đọc hiển thị tên đã chọn, kèm
 * nút kính lúp để mở hộp thoại tìm và nút ✕ để bỏ chọn. Hệ cũ dùng đúng cặp nút
 * này ở màn Nhiệm vụ, Lịch họp và Thư viện văn bản.
 */
export function EntityPickerField({
  kind,
  value,
  onChange,
  placeholder = "---- Chọn ----",
  orgId,
}: {
  kind: "org" | "staff";
  value: PickedEntity | null;
  onChange: (v: PickedEntity | null) => void;
  placeholder?: string;
  /** Giới hạn danh sách nhân sự theo đơn vị (chỉ dùng khi kind = "staff"). */
  orgId?: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex items-center gap-1">
        <input
          readOnly
          className={cn(filterInputClass, "cursor-default bg-muted/40")}
          value={value?.name ?? ""}
          placeholder={placeholder}
          onClick={() => setOpen(true)}
        />
        <button
          type="button"
          title={value ? `Bỏ chọn ${value.name}` : "Bỏ chọn"}
          aria-label="Bỏ chọn"
          disabled={!value}
          onClick={() => onChange(null)}
          className="h-8 w-8 grid place-items-center rounded-sm border border-border text-destructive hover:bg-muted disabled:opacity-40"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title={kind === "org" ? "Chọn đơn vị" : "Chọn cá nhân"}
          aria-label={kind === "org" ? "Chọn đơn vị" : "Chọn cá nhân"}
          onClick={() => setOpen(true)}
          className="h-8 w-8 grid place-items-center rounded-sm border border-border text-muted-foreground hover:bg-muted"
        >
          <Search className="h-3.5 w-3.5" />
        </button>
      </div>
      <EntityPickerDialog
        kind={kind}
        open={open}
        orgId={orgId}
        onClose={() => setOpen(false)}
        onPick={(v) => {
          onChange(v);
          setOpen(false);
        }}
      />
    </>
  );
}

function EntityPickerDialog({
  kind,
  open,
  orgId,
  onClose,
  onPick,
}: {
  kind: "org" | "staff";
  open: boolean;
  orgId?: number;
  onClose: () => void;
  onPick: (v: PickedEntity) => void;
}) {
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");

  const { data, isLoading } = useQuery({
    enabled: open,
    queryKey: ["entity-picker", kind, orgId, applied],
    queryFn: async (): Promise<PickedEntity[]> => {
      if (kind === "org") {
        const rows = await fetchOrgs(applied || undefined);
        return rows.map((o) => ({ id: o.orgId, name: o.name ?? o.code ?? String(o.orgId) }));
      }
      const rows = await fetchSigners({ orgId, q: applied || undefined });
      return rows.map((s) => ({ id: s.staffId, name: `${s.fullName} (${s.loginName})` }));
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={kind === "org" ? "Chọn đơn vị" : "Chọn cá nhân"}
      size="md"
      footer={
        <Button variant="outline" size="md" onClick={onClose}>
          Đóng
        </Button>
      }
    >
      <form
        className="flex gap-2 mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(q);
        }}
      >
        <input
          className={filterInputClass}
          placeholder={kind === "org" ? "Tên đơn vị…" : "Tên hoặc tên đăng nhập…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button type="submit" size="md">
          <Search className="h-4 w-4" /> Tìm
        </Button>
      </form>
      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Đang tải…</div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Không tìm thấy.</div>
      ) : (
        <ul className="divide-y divide-border max-h-80 overflow-y-auto">
          {data!.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onPick(r)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
              >
                {r.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
